<template>
  <div class="scf-sheet" :class="{ 'is-open': open }" role="dialog" aria-modal="true" aria-label="계약 정보 입력">
    <button type="button" class="scf-sheet__backdrop" aria-label="닫기" @click="$emit('close')" />
    <div class="scf-sheet__panel">
      <header class="scf-sheet__head">
        <div class="scf-sheet__head-text">
          <p class="scf-sheet__step">{{ stepIndex + 1 }} / {{ sections.length }}</p>
          <h2 class="scf-sheet__title">{{ current.title }}</h2>
          <p class="scf-sheet__where">{{ current.desc }}</p>
        </div>
        <button type="button" class="scf-sheet__x" @click="$emit('close')">닫기</button>
      </header>

      <div class="scf-sheet__progress" aria-hidden="true">
        <span
          v-for="(s, i) in sections"
          :key="s.id"
          class="scf-sheet__dot"
          :class="{ 'is-on': i === stepIndex, 'is-done': i < stepIndex }"
        />
      </div>

      <div class="scf-sheet__body">
        <!-- 1. 우리 기관 -->
        <section v-show="section === 'party'" class="scf-sheet__sec">
          <div class="scf-field">
            <span class="scf-field__label">기관명</span>
            <input v-model="form.orgName" class="scf-field__input" type="text" autocomplete="organization" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">기관기호<em class="scf-field__where">본문 표 · 별첨 1</em></span>
            <input v-model="form.facilityCode" class="scf-field__input" type="text" inputmode="numeric" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">법인·사업자명</span>
            <input v-model="form.legalName" class="scf-field__input" type="text" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">대표자</span>
            <input v-model="form.representative" class="scf-field__input" type="text" autocomplete="name" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">사업자등록번호</span>
            <input v-model="form.bizNumber" class="scf-field__input" type="text" inputmode="numeric" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">소재지</span>
            <div class="scf-addr">
              <div class="scf-addr__zip">
                <input
                  :value="form.zipcode"
                  class="scf-field__input"
                  type="text"
                  readonly
                  placeholder="우편번호"
                  @click="onSearchAddress"
                />
                <button
                  type="button"
                  class="scf-addr__btn"
                  :disabled="postcodeOpening"
                  @click="onSearchAddress"
                >
                  {{ postcodeOpening ? '여는 중…' : '주소 검색' }}
                </button>
              </div>
              <input
                :value="form.address"
                class="scf-field__input"
                type="text"
                readonly
                placeholder="주소"
                @click="onSearchAddress"
              />
              <input
                v-model="form.addressDetail"
                class="scf-field__input"
                type="text"
                maxlength="80"
                placeholder="상세주소"
                autocomplete="address-line2"
              />
            </div>
          </div>
          <div class="scf-field">
            <span class="scf-field__label">담당자<em class="scf-field__where">별첨 1 · 별첨 3 확인란</em></span>
            <input v-model="form.contactName" class="scf-field__input" type="text" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">이메일<em class="scf-field__where">별첨 1</em></span>
            <input v-model="form.contactEmail" class="scf-field__input" type="email" inputmode="email" autocomplete="email" />
          </div>

          <fieldset class="scf-box">
            <legend>을 | 함우리</legend>
            <p class="scf-sheet__note">피계약자에게 보여주는 계약서에서는 함우리 정보를 고치지 않습니다.</p>
            <p class="scf-fixed">{{ form.partyBLegalName }} · {{ form.partyBBizNumber }} · {{ form.partyBOwner }}</p>
            <p class="scf-fixed">{{ form.partyBAddress }}</p>
            <p class="scf-fixed">{{ form.partyBPhone }}<template v-if="form.partyBOrg"> · {{ form.partyBOrg }}</template></p>
          </fieldset>

          <div class="scf-field scf-field--row">
            <div>
              <span class="scf-field__label">체결일(년)<em class="scf-field__where">본문 서명란</em></span>
              <input v-model="form.signedYear" class="scf-field__input" type="text" inputmode="numeric" placeholder="2026" />
            </div>
            <div>
              <span class="scf-field__label">월</span>
              <input v-model="form.signedMonth" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
            <div>
              <span class="scf-field__label">일</span>
              <input v-model="form.signedDay" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
          </div>
          <fieldset class="scf-box">
            <legend>합의된 별첨 (본문 서명란 표시)</legend>
            <label class="scf-check"><input v-model="form.annexAgreed.a1" type="checkbox" /><span>별첨 1</span></label>
            <label class="scf-check"><input v-model="form.annexAgreed.a2" type="checkbox" /><span>별첨 2</span></label>
            <label class="scf-check"><input v-model="form.annexAgreed.a3" type="checkbox" /><span>별첨 3</span></label>
            <label class="scf-check"><input v-model="form.annexAgreed.a4" type="checkbox" /><span>별첨 4</span></label>
          </fieldset>
        </section>

        <!-- 2. 서비스 선택 -->
        <section v-show="section === 'annex1'" class="scf-sheet__sec">
          <fieldset class="scf-box">
            <legend>임금관리</legend>
            <label class="scf-check">
              <input v-model="form.wageSelect" type="radio" value="selected" />
              <span>선택</span>
            </label>
            <label class="scf-check">
              <input v-model="form.wageSelect" type="radio" value="unselected" />
              <span>미선택</span>
            </label>
            <div v-if="form.wageSelect === 'selected'" class="scf-field scf-field--row">
              <div>
                <span class="scf-field__label">시작연월(년)</span>
                <input v-model="form.wageStartYear" class="scf-field__input" type="text" inputmode="numeric" placeholder="2026" />
              </div>
              <div>
                <span class="scf-field__label">월</span>
                <input v-model="form.wageStartMonth" class="scf-field__input" type="text" inputmode="numeric" placeholder="4" />
              </div>
            </div>
          </fieldset>

          <fieldset class="scf-box">
            <legend>재무회계</legend>
            <label class="scf-check">
              <input v-model="form.acctSelect" type="radio" value="selected" />
              <span>선택</span>
            </label>
            <label class="scf-check">
              <input v-model="form.acctSelect" type="radio" value="unselected" />
              <span>미선택</span>
            </label>
            <div v-if="form.acctSelect === 'selected'" class="scf-field scf-field--row">
              <div>
                <span class="scf-field__label">시작연월(년)</span>
                <input v-model="form.acctStartYear" class="scf-field__input" type="text" inputmode="numeric" placeholder="2026" />
              </div>
              <div>
                <span class="scf-field__label">월</span>
                <input v-model="form.acctStartMonth" class="scf-field__input" type="text" inputmode="numeric" placeholder="4" />
              </div>
            </div>
          </fieldset>

          <div class="scf-field scf-field--row">
            <div>
              <span class="scf-field__label">공통 계약 최초 종료연월(년)</span>
              <input v-model="form.contractEndYear" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
            <div>
              <span class="scf-field__label">월</span>
              <input v-model="form.contractEndMonth" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
          </div>

          <fieldset v-if="form.wageSelect === 'selected'" class="scf-box">
            <legend>임금관리 업무</legend>
            <label class="scf-check"><input v-model="form.wageScopes.baseInput" type="checkbox" /><span>급여기초자료 입력·정리</span></label>
            <label class="scf-check"><input v-model="form.wageScopes.payroll" type="checkbox" /><span>임금 자동산출·급여대장</span></label>
            <label class="scf-check"><input v-model="form.wageScopes.payslip" type="checkbox" /><span>급여명세서 작성 지원</span></label>
            <label class="scf-check"><input v-model="form.wageScopes.insurance" type="checkbox" /><span>사회보험·세무 관련 기초자료</span></label>
            <label class="scf-check"><input v-model="form.wageScopes.other" type="checkbox" /><span>기타</span></label>
            <div v-if="form.wageScopes.other" class="scf-field">
              <span class="scf-field__label">기타 내용</span>
              <input v-model="form.wageScopes.otherText" class="scf-field__input" type="text" />
            </div>
          </fieldset>

          <fieldset v-if="form.acctSelect === 'selected'" class="scf-box">
            <legend>재무회계 업무</legend>
            <label class="scf-check"><input v-model="form.acctScopes.incomeExpense" type="checkbox" /><span>수입·지출자료 정리</span></label>
            <label class="scf-check"><input v-model="form.acctScopes.voucher" type="checkbox" /><span>계정과목 분류·전표입력 지원</span></label>
            <label class="scf-check"><input v-model="form.acctScopes.books" type="checkbox" /><span>월별 장부·관리자료</span></label>
            <label class="scf-check"><input v-model="form.acctScopes.budget" type="checkbox" /><span>예산·결산 기초자료</span></label>
            <label class="scf-check"><input v-model="form.acctScopes.other" type="checkbox" /><span>기타</span></label>
            <div v-if="form.acctScopes.other" class="scf-field">
              <span class="scf-field__label">기타 내용</span>
              <input v-model="form.acctScopes.otherText" class="scf-field__input" type="text" />
            </div>
          </fieldset>

          <fieldset class="scf-box">
            <legend>별도 전문자격자 수행</legend>
            <label class="scf-check"><input v-model="form.specialist" type="radio" value="none" /><span>없음</span></label>
            <label class="scf-check"><input v-model="form.specialist" type="radio" value="yes" /><span>있음</span></label>
            <div v-if="form.specialist === 'yes'" class="scf-field">
              <span class="scf-field__label">별도 계약 주체·업무명</span>
              <input v-model="form.specialistDetail" class="scf-field__input" type="text" />
            </div>
          </fieldset>

          <div class="scf-field">
            <span class="scf-field__label">추가업무(소급정리·현장방문 등)</span>
            <textarea v-model="form.extraWork" class="scf-field__input scf-field__input--area" rows="2" />
          </div>
        </section>

        <!-- 3. 요금 — 작성된 별첨 2를 보여 주기만 한다 -->
        <section v-show="section === 'annex2'" class="scf-sheet__sec">
          <p class="scf-sheet__note">별첨 2는 계약서를 보여 주기 전에 작성한 내용입니다. 여기서는 고치지 않습니다.</p>
          <div v-if="form.annex2Mode === 'text'" class="scf-fixed scf-fixed--pre">{{ form.annex2Text || '적힌 내용이 없습니다.' }}</div>
          <template v-else>
            <p class="scf-fixed">임금관리 {{ wageFeeLabel }}</p>
            <p class="scf-fixed">재무회계 {{ acctFeeLabel }}</p>
            <p v-if="form.monthlyTotal" class="scf-fixed">월 이용료 합계 {{ form.monthlyTotal }}원</p>
          </template>
        </section>

        <!-- 4. 일정 — 앞에서 선택한 서비스만 -->
        <section v-show="section === 'annex3'" class="scf-sheet__sec">
          <template v-if="wageOn">
            <p class="scf-sheet__sub">임금관리</p>
            <div class="scf-field scf-field--row">
              <div>
                <span class="scf-field__label">급여 기초자료 제출일(매월)</span>
                <input v-model="form.wageSubmitDay" class="scf-field__input" type="text" inputmode="numeric" placeholder="5" />
              </div>
              <div>
                <span class="scf-field__label">대장·명세서 제공일(매월)</span>
                <input v-model="form.wageProvideDay" class="scf-field__input" type="text" inputmode="numeric" placeholder="10" />
              </div>
              <div>
                <span class="scf-field__label">최종 확인일(매월)</span>
                <input v-model="form.wageConfirmDay" class="scf-field__input" type="text" inputmode="numeric" placeholder="12" />
              </div>
            </div>
          </template>

          <template v-if="acctOn">
            <p class="scf-sheet__sub">재무회계</p>
            <div class="scf-field scf-field--row">
              <div>
                <span class="scf-field__label">전월 증빙 제출일(매월)</span>
                <input v-model="form.acctSubmitDay" class="scf-field__input" type="text" inputmode="numeric" />
              </div>
              <div>
                <span class="scf-field__label">장부·전표 정리일(매월)</span>
                <input v-model="form.acctProvideDay" class="scf-field__input" type="text" inputmode="numeric" />
              </div>
              <div>
                <span class="scf-field__label">확인일(매월)</span>
                <input v-model="form.acctConfirmDay" class="scf-field__input" type="text" inputmode="numeric" />
              </div>
            </div>
          </template>

          <template v-if="wageOn || acctOn">
            <div class="scf-field">
              <span class="scf-field__label">자료 전달 수단</span>
              <input v-model="form.deliveryMethod" class="scf-field__input" type="text" placeholder="예: 시스템 / 보안 파일공유 / 기타" />
            </div>
            <div class="scf-field">
              <span class="scf-field__label">장애 시 대체 연락처</span>
              <input v-model="form.backupContact" class="scf-field__input" type="text" inputmode="tel" />
            </div>

            <fieldset v-if="wageOn" class="scf-box">
              <legend>갑이 제공하는 자료 — 임금관리 선택 시</legend>
              <label class="scf-check"><input v-model="form.wageDocs.contract" type="checkbox" /><span>근로계약·임금조건</span></label>
              <label class="scf-check"><input v-model="form.wageDocs.attendance" type="checkbox" /><span>근무표·근태</span></label>
              <label class="scf-check"><input v-model="form.wageDocs.hr" type="checkbox" /><span>입사·퇴사·휴직정보</span></label>
              <label class="scf-check"><input v-model="form.wageDocs.allowance" type="checkbox" /><span>수당·공제항목</span></label>
              <label class="scf-check"><input v-model="form.wageDocs.account" type="checkbox" /><span>지급·계좌변경</span></label>
              <label class="scf-check"><input v-model="form.wageDocs.other" type="checkbox" /><span>기타</span></label>
              <div v-if="form.wageDocs.other" class="scf-field">
                <span class="scf-field__label">기타 자료</span>
                <input v-model="form.wageDocs.otherText" class="scf-field__input" type="text" />
              </div>
            </fieldset>

            <fieldset v-if="acctOn" class="scf-box">
              <legend>갑이 제공하는 자료 — 재무회계 선택 시</legend>
              <label class="scf-check"><input v-model="form.acctDocs.transactions" type="checkbox" /><span>거래내역</span></label>
              <label class="scf-check"><input v-model="form.acctDocs.evidence" type="checkbox" /><span>수입·지출 증빙</span></label>
              <label class="scf-check"><input v-model="form.acctDocs.subsidy" type="checkbox" /><span>보조금·장기요양급여 관련 내역</span></label>
              <label class="scf-check"><input v-model="form.acctDocs.prevBooks" type="checkbox" /><span>전월 장부</span></label>
              <label class="scf-check"><input v-model="form.acctDocs.other" type="checkbox" /><span>기타</span></label>
              <div v-if="form.acctDocs.other" class="scf-field">
                <span class="scf-field__label">기타 자료</span>
                <input v-model="form.acctDocs.otherText" class="scf-field__input" type="text" />
              </div>
            </fieldset>

            <div class="scf-field scf-field--row">
              <div>
                <span class="scf-field__label">처리오류 발견 시 통지 경로</span>
                <input v-model="form.errorNotifyPath" class="scf-field__input" type="text" placeholder="예: 담당자 이메일" />
              </div>
              <div>
                <span class="scf-field__label">1차 회신 목표(영업일)</span>
                <input v-model="form.replyBusinessDays" class="scf-field__input" type="text" inputmode="numeric" placeholder="3" />
              </div>
            </div>

            <!-- 갑 담당자는 1단계 「담당자」와 같아 중복 입력을 없앴다.
                 화면·PDF 모두 1단계 담당자가 별첨 3 확인란에 그대로 표시된다 -->
            <div class="scf-field">
              <span class="scf-field__label">을 담당자(함우리)</span>
              <input v-model="form.annex3PartyB" class="scf-field__input" type="text" />
            </div>
            <p class="scf-sheet__note">
              별첨 3 확인란의 「갑 담당자」에는 1단계에서 입력한 담당자가 그대로 표시됩니다.
            </p>
            <div class="scf-field scf-field--row">
              <div>
                <span class="scf-field__label">확인일(년)</span>
                <input v-model="form.annex3ConfirmYear" class="scf-field__input" type="text" inputmode="numeric" />
              </div>
              <div>
                <span class="scf-field__label">월</span>
                <input v-model="form.annex3ConfirmMonth" class="scf-field__input" type="text" inputmode="numeric" />
              </div>
              <div>
                <span class="scf-field__label">일</span>
                <input v-model="form.annex3ConfirmDay" class="scf-field__input" type="text" inputmode="numeric" />
              </div>
            </div>
          </template>
        </section>

        <!-- 5. 개인정보 — 별첨 4 「개인정보 재위탁 현황」 원문 구조 그대로: ①·② 칸은 항상 두고, 맨 아래 「해당 없음」 선택 -->
        <section v-show="section === 'annex4'" class="scf-sheet__sec">
          <p class="scf-sheet__sub">재위탁 업체 ①</p>
          <div class="scf-field">
            <span class="scf-field__label">업체명</span>
            <input v-model="form.reconsign1.vendor" class="scf-field__input" type="text" :disabled="form.reconsignNone" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">업무</span>
            <input v-model="form.reconsign1.work" class="scf-field__input" type="text" :disabled="form.reconsignNone" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">처리항목</span>
            <input v-model="form.reconsign1.items" class="scf-field__input" type="text" :disabled="form.reconsignNone" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">갑 동의일</span>
            <input v-model="form.reconsign1.agreedDate" class="scf-field__input" type="text" placeholder="예: 2026. 1. 15." :disabled="form.reconsignNone" />
          </div>

          <p class="scf-sheet__sub">재위탁 업체 ② (해당 시)</p>
          <div class="scf-field">
            <span class="scf-field__label">업체명</span>
            <input v-model="form.reconsign2.vendor" class="scf-field__input" type="text" :disabled="form.reconsignNone" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">업무</span>
            <input v-model="form.reconsign2.work" class="scf-field__input" type="text" :disabled="form.reconsignNone" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">처리항목</span>
            <input v-model="form.reconsign2.items" class="scf-field__input" type="text" :disabled="form.reconsignNone" />
          </div>
          <div class="scf-field">
            <span class="scf-field__label">갑 동의일</span>
            <input v-model="form.reconsign2.agreedDate" class="scf-field__input" type="text" placeholder="예: 2026. 1. 15." :disabled="form.reconsignNone" />
          </div>

          <label class="scf-check scf-check--block">
            <input v-model="form.reconsignNone" type="checkbox" />
            <span>재위탁이 없는 경우 — 해당 없음</span>
          </label>
          <p class="scf-sheet__note">
            「해당 없음」에 체크하면 ①·②는 빈칸으로 두고, 별첨 4에 「해당 없음」으로 표시됩니다.
            공란은 재위탁 동의로 간주되지 않습니다.
          </p>

          <div class="scf-field scf-field--row">
            <div>
              <span class="scf-field__label">합의일(년)</span>
              <input v-model="form.annex4AgreedYear" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
            <div>
              <span class="scf-field__label">월</span>
              <input v-model="form.annex4AgreedMonth" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
            <div>
              <span class="scf-field__label">일</span>
              <input v-model="form.annex4AgreedDay" class="scf-field__input" type="text" inputmode="numeric" />
            </div>
          </div>
        </section>

        <!-- 별첨 5(종료 인수인계)는 해지 시점 서류라 입력 단계에서 뺀다. 종료 플로우에서 다시 다룬다. -->

        <!-- 6. 전자서명 — 계약서·별첨의 갑 (서명) 자리에 들어간다. 성명은 원문대로 1단계의 대표자를 사용 -->
        <section v-show="section === 'sign'" class="scf-sheet__sec">
          <p class="scf-sheet__sub">갑 전자서명</p>
          <SignaturePad v-model="form.partyASignImage" />
          <p class="scf-sheet__note">
            여기에 서명하면 계약서 본문과 별첨의 「갑 (서명)」 자리에 서명이 표시되고, PDF에도 그대로 들어갑니다.
            서명 옆 성명은 1단계에서 입력한 「대표자」가 표시됩니다.
          </p>
        </section>
      </div>

      <footer class="scf-sheet__foot">
        <button type="button" class="scf-sheet__ghost" :disabled="stepIndex === 0" @click="goPrev">이전</button>
        <button v-if="stepIndex < sections.length - 1" type="button" class="scf-sheet__primary" @click="goNext">
          다음
        </button>
        <button v-else type="button" class="scf-sheet__primary" @click="$emit('close')">
          완료
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ServiceContractForm } from '@/composables/useServiceContractForm'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import SignaturePad from '@/components/contract/SignaturePad.vue'

type StepId = 'party' | 'annex1' | 'annex2' | 'annex3' | 'annex4' | 'sign'

const props = withDefaults(defineProps<{
  open: boolean
  form: ServiceContractForm
  /** 열 때 처음 보여 줄 단계 — 문서에서 칸을 눌러 열면 그 단계부터 */
  initialSection?: StepId
}>(), { initialSection: 'party' })

defineEmits<{
  close: []
}>()

const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()

async function onSearchAddress() {
  try {
    const result = await searchPostcode()
    if (!result) return
    props.form.zipcode = result.zipcode
    props.form.address = [result.address, result.extra].filter(Boolean).join(' ')
    /* 상세주소는 사용자가 이어서 입력 — 포커스는 상세칸으로 */
  } catch (e) {
    window.alert(e instanceof Error ? e.message : '주소 검색을 열지 못했습니다.')
  }
}

const section = ref<StepId>('party')

const wageOn = computed(() => props.form.wageSelect === 'selected')
const acctOn = computed(() => props.form.acctSelect === 'selected')

function feeLabel(kind: 'wage' | 'acct') {
  const fee = kind === 'wage' ? props.form.wageFee : props.form.acctFee
  const selected = kind === 'wage' ? props.form.wageSelect === 'selected' : props.form.acctSelect === 'selected'
  if (!selected || fee === 'none') return '해당 없음'
  if (fee === 'flat') {
    const amount = kind === 'wage' ? props.form.wageFlat : props.form.acctFlat
    return amount ? `월정액 ${amount}원` : '월정액'
  }
  if (fee === 'headcount') {
    const base = props.form.wageBaseHeadcount
    const extra = props.form.wageExtraPerPerson
    return base || extra ? `인원연동 (기본 ${base || '—'}명, 초과 1명당 ${extra || '—'}원)` : '인원연동'
  }
  if (fee === 'volume') return props.form.acctVolume ? `건수연동 ${props.form.acctVolume}` : '건수연동'
  if (fee === 'custom') {
    const formula = kind === 'wage' ? props.form.wageCustomFormula : props.form.acctCustomFormula
    return formula ? `별도 산식 ${formula}` : '별도 산식'
  }
  return '작성된 내용'
}

const wageFeeLabel = computed(() => feeLabel('wage'))
const acctFeeLabel = computed(() => feeLabel('acct'))


const sections = computed(() => [
  {
    id: 'party' as const,
    title: '기관 정보',
    desc: '본문 「계약 당사자」 표와 서명란, 각 별첨의 「갑」 정보에 공통으로 들어갑니다.',
  },
  {
    id: 'annex1' as const,
    title: '서비스 선택',
    desc: '별첨 1 「서비스 내용·기간」에 들어갑니다.',
  },
  {
    id: 'annex2' as const,
    title: wageOn.value && !acctOn.value
      ? '임금관리 요금'
      : acctOn.value && !wageOn.value
        ? '재무회계 요금'
        : '이용요금',
    desc: '별첨 2는 이미 작성된 내용입니다. 이 단계에서는 고치지 않습니다.',
  },
  {
    id: 'annex3' as const,
    title: wageOn.value && !acctOn.value
      ? '임금관리 일정'
      : acctOn.value && !wageOn.value
        ? '재무회계 일정'
        : '업무 일정',
    desc: '별첨 3 「업무 일정과 자료 제공」에 들어갑니다.',
  },
  {
    id: 'annex4' as const,
    title: '개인정보 재위탁',
    desc: '별첨 4 하단 「개인정보 재위탁 현황」에 들어갑니다.',
  },
  /* 별첨 5(서비스 종료)는 해지 시점 서류라 입력 단계에서 뺀다 */
  {
    id: 'sign' as const,
    title: '서명',
    desc: '본문과 별첨의 「갑 (서명)」 자리에 들어가고, PDF에도 그대로 표시됩니다.',
  },
])

const stepIndex = computed(() => sections.value.findIndex((s) => s.id === section.value))
const current = computed(() => sections.value[stepIndex.value] || sections.value[0])

watch(
  () => props.open,
  (open) => {
    if (open) {
      section.value = props.initialSection
    }
  },
)

/* 미선택 서비스 정리 규칙은 useServiceContractForm에 있다 (문서 직접 편집과 경로 공유) */

function goNext() {
  const i = stepIndex.value
  if (section.value === 'annex1' && !wageOn.value && !acctOn.value) {
    window.alert('임금관리 또는 재무회계를 선택해 주세요.')
    return
  }
  if (i < sections.value.length - 1) section.value = sections.value[i + 1].id
}

function goPrev() {
  const i = stepIndex.value
  if (i > 0) section.value = sections.value[i - 1].id
}

</script>

<style lang="scss" src="./ServiceContractFormSheet.scss"></style>
